import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function DiscountsAdmin() {
  const [discounts, setDiscounts] = useState([]);
  const [form, setForm] = useState({ code: "", percent_off: "" });

  const refresh = () => api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/discounts", { code: form.code, percent_off: Number(form.percent_off) });
      toast.success("Discount code created — customers can apply it at checkout.");
      setForm({ code: "", percent_off: "" });
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/discounts/${id}`);
      toast.success("Code deactivated.");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-discounts">
      <h1 className="font-display text-4xl text-white mb-2">Discount Codes</h1>
      <p className="text-sm text-zinc-500 mb-8">Codes are created in Stripe and applied by customers during checkout (percentage off the whole order).</p>

      <form onSubmit={create} className="flex flex-wrap gap-3 items-end border border-white/10 bg-[#0A0A0A] p-5 mb-8">
        <div>
          <Label className="text-xs text-zinc-400">Code</Label>
          <Input data-testid="discount-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="SUMMER20" className="bg-white/5 border-white/10 mt-1 w-44 uppercase" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">% Off</Label>
          <Input data-testid="discount-percent" required type="number" min="1" max="100" value={form.percent_off}
            onChange={(e) => setForm({ ...form, percent_off: e.target.value })} className="bg-white/5 border-white/10 mt-1 w-28" />
        </div>
        <button data-testid="discount-create-btn" type="submit"
          className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-5 py-2.5 transition-colors">
          <Plus className="w-4 h-4" /> Create Code
        </button>
      </form>

      {discounts.filter((d) => d.active).length === 0 ? (
        <p className="text-sm text-zinc-500">No active discount codes.</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5">
          {discounts.filter((d) => d.active).map((d) => (
            <div key={d.id} data-testid={`discount-row-${d.id}`} className="bg-[#0A0A0A] p-4 flex items-center justify-between">
              <div>
                <span className="font-display text-2xl text-white tracking-wide">{d.code}</span>
                <span className="text-sm text-brand ml-3">{d.percent_off}% off</span>
              </div>
              <button data-testid={`discount-delete-${d.id}`} onClick={() => remove(d.id)}
                className="flex items-center gap-1.5 text-xs text-red-400 border border-white/15 hover:border-red-400 px-3 py-2 transition-colors">
                <Trash2 className="w-3 h-3" /> Deactivate
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
